import React, { useState, useEffect, useRef } from "react";
import { Typography, Grid, Paper, IconButton, Box, Chip, Autocomplete, TextField, Button, Switch, FormControlLabel, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  createCompanyAction,
  updateCompanyAction,
} from "../../redux/actions/company";
import CustomTextField from "../../form/CustomTextField";
import CustomDateField from "../../form/CustomDateField";
import strings from "../../i18n/language";
import Fab from "@mui/material/Fab";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import InfoIcon from "@mui/icons-material/Info";
import CancelIcon from "@mui/icons-material/Cancel";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import dayjs from "dayjs";
import { instance } from "../../axios";

const CompanyEdit = () => {
  // State for editable fields
  const navigate = useNavigate();

  const [name, setName] = useState(null);
  const [phone, setPhone] = useState(null);
  const [mail, setMail] = useState(null);
  const [comment, setComment] = useState(null);
  const [startContract, setStartContract] = useState(null);
  const [errorName, setErrorName] = useState(false);
  const [identityNumber, setIdentityNumber] = useState(null);
  const [errorIdentityNumber, setErrorIdentityNumber] = useState(false);
  const [address, setAddress] = useState(null);
  const [modules, setModules] = useState([]);
  const [medecinReferentId, setMedecinReferentId] = useState(null);
  const [medecins, setMedecins] = useState([]);
  const [customBranding, setCustomBranding] = useState(false);
  const [pdfLogoKey, setPdfLogoKey] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  const [valid, setValid] = useState(false);
  const company = useSelector((state) => state.company.company);
  const dispatch = useDispatch();

  useEffect(() => {
    instance.get("/user/medecin?limit=200&offset=0&query=").then((res) => {
      setMedecins(res.data?.data || []);
    });
  }, []);

  useEffect(() => {
    if (company) {
      setName(company.name);
      setPhone(company.phone);
      setMail(company.mail);
      setComment(company.comment);
      setIdentityNumber(company.identityNumber);
      const mods = company.modules;
      setModules(Array.isArray(mods) ? mods : (typeof mods === "string" ? (() => { try { return JSON.parse(mods); } catch { return []; } })() : []));
      setAddress(company.address ?? null);
      setMedecinReferentId(company.medecinReferentId ?? null);
      setCustomBranding(!!company.customBranding);
      setPdfLogoKey(company.pdfLogoKey || null);
    }
  }, [company, dispatch]);

  useEffect(() => {
    setValid(!errorName && !errorIdentityNumber);
  }, [errorName, errorIdentityNumber]);

  useEffect(() => {
    if (!identityNumber) { setErrorIdentityNumber(true); return; }
    setErrorIdentityNumber(!/^[JVGEP]-\d{8,9}$/i.test(identityNumber.trim()));
  }, [identityNumber]);

  useEffect(() => {
    const alphanumericRegex = /^[a-z0-9\sáéíóúñüäöüß.,;:!?'"\-()%]+$/i;
    if (name && name.length > 4 && name.length < 60) {
      if (alphanumericRegex.test(name)) {
        setErrorName(false);
      } else {
        setErrorName(true);
      }
    } else setErrorName(true);
  }, [name]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !company?.id) return;
    const formData = new FormData();
    formData.append("logo", file);
    setLogoUploading(true);
    try {
      const res = await instance.post(`/company/${company.id}/logo-pdf`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPdfLogoKey(res.data.pdfLogoKey);
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleLogoDelete = async () => {
    if (!company?.id) return;
    await instance.delete(`/company/${company.id}/logo-pdf`);
    setPdfLogoKey(null);
  };

  const save = () => {
    if (company && company.id)
      dispatch(
        updateCompanyAction({
          name: name,
          identityNumber: identityNumber,
          phone: phone,
          mail: mail,
          comment: comment,
          address: address,
          modules: modules,
          medecinReferentId: medecinReferentId,
          customBranding: customBranding,
          id: company.id,
        })
      );
    else
      dispatch(
        createCompanyAction({
          name: name,
          identityNumber: identityNumber,
          phone: phone,
          mail: mail,
          startContract: startContract,
          comment: comment,
          modules: modules,
          medecinReferentId: medecinReferentId,
        })
      );
    navigate("/companies");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate("/companies")} sx={{ alignSelf: "flex-start", mb: 1, color: "text.secondary" }}>
        Volver
      </Button>
      <Paper
        sx={{
          p: 2,
          width: "80%",
          backgroundColor: "white",
        }}
      >
        <Grid container justifyContent="center" margin="normal" mt={2}>
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="name"
              label={strings.LABEL_NAME}
              name="name"
              value={name}
              onChange={setName}
              autoFocus
            />
          </Grid>
          {name && (
            <Grid item xs={8} mt={1}>
              <Typography color={errorName ? "error" : "success.main"}>
                {strings.REQUIREMENT_NAME_COMPANY}
              </Typography>
            </Grid>
          )}
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="name"
              label={strings.LABEL_RIF}
              name="name"
              value={identityNumber}
              onChange={setIdentityNumber}
              autoFocus
            />
          </Grid>
          {identityNumber && (
            <Grid item xs={8} mt={1}>
              <Typography color={errorIdentityNumber ? "error" : "success.main"}>
                {strings.REQUIREMENT_RIF}
              </Typography>
            </Grid>
          )}
          {!identityNumber && (
            <Grid item xs={8} mt={1} display="flex" alignItems="center">
              <InfoIcon style={{ marginRight: 8 }} />
              <Typography>{strings.REQUIREMENT_RIF}</Typography>
            </Grid>
          )}
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="phone"
              label={strings.LABEL_PHONE}
              name="phone"
              value={phone}
              onChange={setPhone}
              autoFocus
            />
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="mail"
              label={strings.LABEL_MAIL}
              name="mail"
              value={mail}
              onChange={setMail}
              autoFocus
            />
          </Grid>
          <Grid item xs={8} mt={2}>
            <TextField
              fullWidth
              variant="filled"
              margin="normal"
              id="address"
              label={strings.LABEL_ADDRESS}
              name="address"
              value={address || ""}
              onChange={(e) => setAddress(e.target.value)}
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={8} mt={2} display="flex">
            <CustomTextField
              margin="normal"
              fullWidth
              id="comment"
              label={strings.LABEL_COMMENT}
              name="comment"
              value={comment}
              onChange={setComment}
              autoFocus
            />
          </Grid>
          <Grid item xs={8} mt={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>Modules</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {["Servital", "Laboral"].map((mod) => {
                const active = modules.includes(mod);
                return (
                  <Chip
                    key={mod}
                    label={mod}
                    color={active ? "primary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    onClick={() =>
                      setModules(active ? modules.filter((m) => m !== mod) : [...modules, mod])
                    }
                    clickable
                  />
                );
              })}
            </Box>
          </Grid>
          {modules.includes("Laboral") && (
            <Grid item xs={8} mt={2}>
              <Autocomplete
                options={medecins.filter((m) => m.medicinaOcupacional)}
                getOptionLabel={(o) => o.displayName + (o.specialty ? ` — ${o.specialty}` : "")}
                value={medecins.find((m) => m.id === medecinReferentId) || null}
                onChange={(_, val) => setMedecinReferentId(val ? val.id : null)}
                renderInput={(params) => (
                  <TextField {...params} label="Médico referente (módulo laboral)" size="small" />
                )}
                isOptionEqualToValue={(o, v) => o.id === v.id}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Su firma será utilizada en las constancias de evaluación y reposo médico.
              </Typography>
            </Grid>
          )}
          <Grid item xs={8} mt={2}>
            <Typography variant="body2" color="text.secondary" mb={1}>Branding personalizado en exportaciones</Typography>
            <FormControlLabel
              control={<Switch checked={customBranding} onChange={(e) => setCustomBranding(e.target.checked)} />}
              label="Usar logo y datos de la empresa en los PDF"
            />
            {customBranding && company?.id && (
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleLogoUpload}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={logoUploading ? <CircularProgress size={14} /> : <UploadIcon />}
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                >
                  {pdfLogoKey ? "Reemplazar logo" : "Cargar logo"}
                </Button>
                {pdfLogoKey && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleLogoDelete}
                  >
                    Eliminar logo
                  </Button>
                )}
                <Typography variant="caption" color={pdfLogoKey ? "success.main" : "text.secondary"}>
                  {pdfLogoKey ? "Logo personalizado cargado" : "Sin logo — se usará el logo de Servital"}
                </Typography>
              </Box>
            )}
            {customBranding && !company?.id && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                Guarda la empresa primero para poder cargar el logo.
              </Typography>
            )}
          </Grid>
          <Grid item xs={8} display="flex">
            <IconButton
              onClick={() => {
                setStartContract(dayjs(new Date()));
              }}
            >
              <AutoFixNormalIcon></AutoFixNormalIcon>
            </IconButton>
            <CustomDateField
              value={startContract}
              onChange={setStartContract}
              margin="normal"
              isValid={!!startContract}
              label={strings.LABEL_START_CONTRACT}
            />
          </Grid>
          <Grid item xs={8} mt={1} display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography variant="body2" color="text.secondary">
              Opcional — si se indica, se creará automáticamente una suscripción para esta empresa.
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      <div
        style={{
          alignSelf: "flex-end",
          flexDirection: "column",
          display: "flex",
          marginLeft: 10,
        }}
      >
        <Fab color="error" aria-label="cancel" disabled={true}>
          <CancelIcon />
        </Fab>
        <Fab
          color="primary"
          aria-label="add"
          style={{
            marginTop: 10,
          }}
          onClick={save}
          disabled={!valid}
        >
          <SaveIcon />
        </Fab>
      </div>
    </Box>
  );
};

export default CompanyEdit;
